import { useEffect, useContext, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import TextInput from "../../Common/TextInput/TextInput";
import SelectInput from "../../Common/Select-Input/SelectInput";
import AutoComplete from "../../Common/AutoComplete/AutoComplete";
import DynamicCheckbox from "../../Common/Checkbox/Checkbox";
import backArrowIcon from "../../Assets/Images/arrow-back-icon.png";
import Button from "../../Common/Button/Button";
import OptionData from "../../Assets/Json/StaticOptions.json";
import { DynamicContext } from "../../Context/DynamicContext";
import MonsterConfig from "./MonsterConfig.json";
import Card from "../../Common/Card/Card";

import {
  MONSTER_SEARCH_BASE_URL,
  MONSTER_TOKEN_URL,
  MONSTER_SEARCH,
  MONSTER_VIEW,
  WEB_SOCKET_URL,
  UPDATE_CANDIDATE_DETAILS,
  GET_CANDIDATES_BY_NOTES,
  VYLABS_BASE_URL,
  GET_CANDIDATE_BY_ID,
  GET_VENDOR_DETAILS_BY_ID,
  CCREATE_CAN,
  VENDORS_SEARCH,
  LINK_CANDIDATE_RESUME,
} from "../../helper/url_helper";
import "./MonsterSearch.css";
import { parseMonsterResumeHeadless } from "./MonsterResumeParser";
import CandidateLightIcon from "../../Assets/Images/candidates-header-icon.svg";
import CandidateDarkIcon from "../../Assets/Images/candidates-header-darkicon.svg";
import { Toast } from "../../Common/Toast-Snackbar/ToastSnackbar";
import CandidateDetailsContainer from "../../Common/Candidate-Details-Container/CandidateDetailsContainer";
import SidePopup from "../../Common/SidePopup/SidePopup";
import AnimationPlayer from "../../Common/Animation-Player/AnimationPlayer";
import VybogTALLoaderAnimation from "../../Common/Dynamic-Animations/DynamicAnimations";
import CustomTooltip from "../../Common/Tooltip/Tooltip";
import infoIcon from "../../Assets/Images/infoicon.svg";
import { get, post } from "../../helper/Api_helper";
import DynamicSkeleton from "../../Common/Skeleton/Skeleton";
import envConfig from "../../helper/env_config";
import NoDataFound from "../../Assets/Json/nodata-found-robot-animation.json";
function MonsterSearch() {
  const { isDarkMode } = useContext(DynamicContext);
  const { StaticOptions = {} } = OptionData;
  const [autoCompleteKey, setAutoCompleteKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isProcessingView, setIsProcessingView] = useState(false);
  const [localCandidate, setLocalCandidate] = useState(null);
  const [isEditingCandidate, setIsEditingCandidate] = useState(false);
  const [originalCandidateData, setOriginalCandidateData] = useState(null);
  const [editedCandidateData, setEditedCandidateData] = useState(null);
  const [fallbackMonsterPopupOpen, setFallbackMonsterPopupOpen] =
    useState(false);
  const [selectedMonsterId, setSelectedMonsterId] = useState(null);
  const [lastProfileDetails, setLastProfileDetails] = useState(null);
  const [wsRef, setWsRef] = useState(null);
  const [wsPingInterval, setWsPingInterval] = useState(null);
  const [wsPingTimeout, setWsPingTimeout] = useState(null);
  const [abortController, setAbortController] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);
  const observer = useRef();
  const navigate = useNavigate();
  const [parsedMap, setParsedMap] = useState(() => {
    try {
      const raw = localStorage.getItem("monsterCandidateIdMap");
      return raw ? JSON.parse(raw) : {};
    } catch (_) {
      return {};
    }
  });
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [vendorList, setVendorList] = useState([]);
  const [vendorOptionLoading, setVendorOptionLoading] = useState(false);
  const [assignedVendor, setAssignedVendor] = useState(null);
  const toggleAdvancedSearch = () => setShowAdvancedSearch(!showAdvancedSearch);

  const {
    skills = [],
    MonsterNoticePeriods = [],
    MonsterGenders = [],
  } = StaticOptions;

  const fetchVendors = async (inputValue = "") => {
    try {
      setVendorOptionLoading(true);
      const response = await get(
        VYLABS_BASE_URL,
        `${VENDORS_SEARCH}?vendor_name=${encodeURIComponent(inputValue)}`,
      );

      const { vendors = [] } = response;
      const fetchedVendors = Array.isArray(vendors)
        ? vendors
            .filter((v) => v?.id && v?.name)
            .map((v) => ({
              label: v.contact_person
                ? `${v.name} - ${v.contact_person}`
                : v.name,
              value: v.id,
            }))
        : [];

      setVendorList(fetchedVendors);
    } catch (err) {
      console.error("fetchVendors error", err);
      setVendorList([]);
    } finally {
      setVendorOptionLoading(false);
    }
  };

  const handleVendorInputChange = (_, value) => {
    if (value && value.length >= 3) {
      fetchVendors(value);
    } else {
      setVendorList([]);
    }
  };

  const handleEditCandidate = () => {
    if (!localCandidate) return;

    if (localCandidate?.isTemp) {
      Toast("error", "Please wait until candidate is fully saved.");
      return;
    }

    const getValidExperience = (value) => {
      const num = Number(value);
      if (isNaN(num)) return "";
      if (num < 0) return 0;
      return num;
    };

    const experienceValue = getValidExperience(
      localCandidate?.experience ?? localCandidate?.years_of_experience,
    );

    const formattedCandidate = {
      ...localCandidate,
      name: localCandidate.name || "",
      email: localCandidate.email || "",
      secondaryEmail: localCandidate.secondaryEmail || "",
      phone: localCandidate.phone || "",
      secondaryPhone: localCandidate.secondaryPhone || "",
      skills: Array.isArray(localCandidate.skills) ? localCandidate.skills : [],
      designation: localCandidate.designation || "",
      experience: experienceValue,
      city: localCandidate.city || "",
      state: localCandidate.state || "",
      country: localCandidate.country || "",
      lineAddress1: localCandidate.lineAddress1 || "",
      lineAddress2: localCandidate.lineAddress2 || "",
      zipCode: localCandidate.zipCode || "",
      vendorID: localCandidate.vendorID || "",
      preferredSalary: localCandidate.preferredSalary || "",
    };

    setOriginalCandidateData({ ...localCandidate });
    setEditedCandidateData(formattedCandidate);
    setIsEditingCandidate(true);
  };

  const handleCandidateChange = (data) => {
    setEditedCandidateData((prev) => ({ ...prev, ...data }));
  };

  const saveCandidateChanges = async () => {
    if (!editedCandidateData) {
      Toast("error", "No changes to save");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const candidateData = { id: localCandidate.id };

      Object.keys(editedCandidateData).forEach((key) => {
        if (editedCandidateData[key] !== originalCandidateData[key]) {
          candidateData[key] = editedCandidateData[key];
        }
      });

      if (Object.keys(candidateData).length === 1) {
        Toast("info", "No changes were made.");
        setIsEditingCandidate(false);
        return;
      }

      if (candidateData.preferredSalary) {
        candidateData.preferredSalary = Number(candidateData.preferredSalary);
      }

      if (candidateData.experience !== undefined) {
        candidateData.yearsOfExperience = Number(candidateData.experience);
        delete candidateData.experience;
      }

      Object.keys(candidateData).forEach((key) => {
        if (candidateData[key] === "" || candidateData[key] == null) {
          delete candidateData[key];
        }
      });

      await post(
        VYLABS_BASE_URL,
        `${UPDATE_CANDIDATE_DETAILS}`,
        candidateData,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      Toast("success", "Candidate updated successfully.");
      const full = await fetchCandidateById(editedCandidateData.id);
      setLocalCandidate(full || { ...localCandidate, ...candidateData });
      setIsEditingCandidate(false);
      setOriginalCandidateData(null);
      setEditedCandidateData(null);
    } catch (error) {
      console.error("Error updating candidate:", error);
      Toast("error", "Failed to update candidate");
    }
  };

  const resetCandidateEdit = () => {
    if (originalCandidateData) {
      setLocalCandidate(originalCandidateData);
    }
    setIsEditingCandidate(false);
    setOriginalCandidateData(null);
    setEditedCandidateData(null);
  };

  const createCandidateFromFallback = async (
    monsterId,
    fileContent,
    profile,
    fileId,
    status,
  ) => {
    if (!fileContent) {
      console.error("[Monster Fallback] Failed: No file content provided.");
      return null;
    }

    const toStringOrNull = (v) => {
      if (v == null) return null;
      if (Array.isArray(v)) return v.length ? String(v[0]).trim() : null;
      return String(v).trim();
    };

    const extractInfo = (text, regex) => {
      const match = text?.match?.(regex);
      return match ? match[1] : null;
    };

    const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || "");

    const rawName =
      profile?.name ||
      `${profile?.personal_details?.[0]?.first_name || ""} ${
        profile?.personal_details?.[0]?.last_name || ""
      }`.trim() ||
      extractInfo(fileContent, /(?:^|\n)\s*(?:Name|NAME)\s*[:\s]+(.+)/i);

    const rawEmail =
      profile?.email?.[0]?.id ||
      extractInfo(
        fileContent,
        /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/,
      );

    const rawPhone =
      (profile?.mobile_details?.[0]?.number
        ? `+${profile?.mobile_details?.[0]?.country_code || ""} ${
            profile?.mobile_details?.[0]?.number
          }`.trim()
        : "") ||
      extractInfo(
        fileContent,
        /((?:\+?\d{1,3}[-.\s]?)?(?:\(?\d{2,4}\)?[-.\s]?)?\d{3,4}[-.\s]?\d{4,6})/,
      );

    const name = toStringOrNull(rawName);
    const email = toStringOrNull(rawEmail);
    let phone = toStringOrNull(rawPhone);

    if (phone) {
      phone = phone.replace(/[^\d+]/g, "");
    }

    const locationArray = Array.isArray(profile?.current_location)
      ? profile?.current_location
      : [];
    const locationText =
      locationArray[0]?.text || profile?.aggregate_locations?.[0] || "";

    let city = (locationText || "").split(",")[0].trim();
    let state = "";
    let country = "";

    if (profile?.current_location_primary_group_tag) {
      country = profile.current_location_primary_group_tag;
    } else if (profile?.current_country?.text) {
      country = profile.current_country.text;
    } else if (
      Array.isArray(profile?.nationality) &&
      profile.nationality.length > 0
    ) {
      country = profile.nationality[0]?.text || "";
    }

    let skills = Array.isArray(profile?.skills)
      ? profile.skills.map((s) => s?.text || s?.value || s).filter(Boolean)
      : [];

    if (/Skills|SKILLS/i.test(fileContent)) {
      const skillsSection = fileContent.split(/Skills|SKILLS/i)[1];
      if (skillsSection) {
        const extractedSkills =
          skillsSection
            .split("\n")
            .slice(0, 10)
            .join(" ")
            .match(/[A-Za-z+#.]+(?:\s+[A-Za-z+#.]+)*/g) || [];
        for (const s of extractedSkills) {
          const val = String(s).trim();
          if (
            val.length > 1 &&
            !skills.some((k) => k.toLowerCase() === val.toLowerCase())
          ) {
            skills.push(val);
          }
        }
      }
    }

    skills = [
      ...new Set(skills.map((s) => String(s).trim()).filter(Boolean)),
    ].slice(0, 20);

    if (!name || !isValidEmail(email)) {
      console.error(
        "[Monster Fallback] Failed: Could not extract valid name or email.",
        { name, email },
      );
      return null;
    }

    if (!name || !email || !phone) {
      return null;
    }

    const appendToCandidateId = "monster_" + String(monsterId).trim();

    const currentEmployment = Array.isArray(profile?.current_employment)
      ? profile?.current_employment?.[0]
      : profile?.current_employment || {};

    const designation =
      currentEmployment?.designation?.text ||
      currentEmployment?.designation ||
      "";

    const experienceArray = Array.isArray(profile?.experience)
      ? profile?.experience
      : [];
    const finalExperienceArray = Array.isArray(profile?.total_experience)
      ? profile?.total_experience
      : [];
    const yearsExp =
      experienceArray[0]?.value ||
      finalExperienceArray[0]?.value ||
      profile?.experience?.[0]?.value ||
      0;

    try {
      const payload = {
        name,
        email: email,
        phone: phone,
        skills,
        designation: designation,
        city: city,
        state: state,
        country: country,
        status: "New Profile",
        source: "Monster",
        vendorID: envConfig.monsterVendorID,
        notes: appendToCandidateId,
        yearsOfExperience: yearsExp,
      };

      if (status === "incomplete" && fileId) {
        payload.fileID = fileId;
      }

      const response = await post(VYLABS_BASE_URL, CCREATE_CAN, payload);
      return response?.id || null;
    } catch (error) {
      console.error("[Monster Fallback] Error creating candidate:", error);
      return null;
    }
  };

  const fetchNewMonsterToken = async () => {
    try {
      const response = await post(
        MONSTER_SEARCH_BASE_URL,
        MONSTER_TOKEN_URL,
        null,
      );
      if (response?.authDetails) {
        const authDetails =
          typeof response.authDetails === "string"
            ? JSON.parse(response.authDetails)
            : response.authDetails;
        const token = authDetails.token;
        if (token) {
          localStorage.setItem("monstertokenInd", token);
          return token;
        }
        throw new Error("No token found in response");
      }
      throw new Error("No authDetails found in response");
    } catch (err) {
      throw err;
    }
  };

  const isTokenInvalidError = (error) => {
    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.message ||
      JSON.stringify(error.response?.data) ||
      "";
    return (
      error.response?.status === 500 &&
      errorMessage.includes("Token is invalid")
    );
  };

  function htmlToTextFile(html, fileName) {
    const blob = new Blob([html || ""], { type: "text/plain" });
    const file = new File([blob], fileName, { type: "text/plain" });
    file.localUrl = URL.createObjectURL(blob);
    return file;
  }
  function buildHtmlFromProfile(profile) {
    try {
      const name =
        profile?.name ||
        `${profile?.personal_details?.[0]?.first_name || ""} ${
          profile?.personal_details?.[0]?.last_name || ""
        }`.trim();
      const email = profile?.email?.[0]?.id || "";
      const phone = profile?.mobile_details?.[0]?.number
        ? `+${profile?.mobile_details?.[0]?.country_code || ""} ${
            profile?.mobile_details?.[0]?.number
          }`.trim()
        : "";
      const location =
        (Array.isArray(profile?.current_location)
          ? profile?.current_location?.[0]?.text
          : profile?.current_location?.text) || "";
      const summary = Array.isArray(profile?.title)
        ? profile?.title?.[0]?.value
        : profile?.title?.value || "";
      const resumeText = profile?.resume_text || "";
      const skills = (
        Array.isArray(profile?.skills)
          ? profile.skills.map((s) => s?.text || s?.value || s).filter(Boolean)
          : []
      ).join(", ");
      const edu = Array.isArray(profile?.educations)
        ? profile.educations
            .map(
              (e) =>
                `${e.degree || ""} - ${e.college || ""} (${
                  e.passing_year || "N/A"
                })`,
            )
            .join("<br/>")
        : "";
      const currEmp =
        Array.isArray(profile?.current_employment) &&
        profile.current_employment[0]
          ? profile.current_employment[0]
          : null;
      const empBlock = currEmp
        ? `${
            (currEmp.employer && (currEmp.employer.text || currEmp.employer)) ||
            ""
          } - ${
            (currEmp.designation &&
              (currEmp.designation.text || currEmp.designation)) ||
            ""
          }<br/>${currEmp.description || ""}`
        : "";

      const safe = (s) => (typeof s === "string" ? s : "");
      const body = `
        <div style="font-family: Arial, sans-serif; font-size: 12pt; line-height: 1.4;">
          <h1 style="margin: 0 0 8px 0; font-size: 18pt;">${safe(name)}</h1>
          <div style="margin-bottom: 10px; color: #555;">
            ${safe(email)}${email && phone ? " | " : ""}${safe(phone)}${
              (email || phone) && location ? " | " : ""
            }${safe(location)}
          </div>
          ${
            summary
              ? `<h2 style="font-size:14pt; margin:16px 0 6px;">Summary</h2><div>${safe(
                  summary,
                )}</div>`
              : ""
          }
          ${
            skills
              ? `<h2 style="font-size:14pt; margin:16px 0 6px;">Skills</h2><div>${safe(
                  skills,
                )}</div>`
              : ""
          }
          ${
            empBlock
              ? `<h2 style="font-size:14pt; margin:16px 0 6px;">Experience</h2><div>${empBlock}</div>`
              : ""
          }
          ${
            edu
              ? `<h2 style="font-size:14pt; margin:16px 0 6px;">Education</h2><div>${edu}</div>`
              : ""
          }
          ${
            resumeText
              ? `<h2 style="font-size:14pt; margin:16px 0 6px;">Resume</h2><pre style="white-space: pre-wrap; font-family: inherit;">${safe(
                  resumeText,
                )}</pre>`
              : ""
          }
        </div>
      `;
      return body;
    } catch {
      return "";
    }
  }
  const makeMonsterApiCall = async (apiCallFunction) => {
    try {
      const token = localStorage.getItem("monstertokenInd");
      return await apiCallFunction(token);
    } catch (error) {
      if (isTokenInvalidError(error)) {
        try {
          localStorage.removeItem("monstertokenInd");
          const newToken = await fetchNewMonsterToken();
          return await apiCallFunction(newToken);
        } catch (refreshError) {
          throw refreshError;
        }
      } else {
        throw error;
      }
    }
  };

  useEffect(() => {
    const initializeMonsterToken = async () => {
      const existingToken = localStorage.getItem("monstertokenInd");
      if (!existingToken) {
        try {
          await fetchNewMonsterToken();
        } catch (error) {
          console.error("Failed to initialize Monster token:", error);
        }
      }
    };
    initializeMonsterToken();
  }, []);

  const validationSchema = Yup.object({
    keywords: Yup.string().max(200, "Max 200 characters"),
    yearsExpFrom: Yup.number().min(0).max(50).nullable(),
    yearsExpTo: Yup.number()
      .min(Yup.ref("yearsExpFrom"), "Must be ≥ From")
      .max(50)
      .nullable(),
  });

  function filterEmpty(obj) {
    return Object.entries(obj).reduce((acc, [key, val]) => {
      const isEmptyArray = Array.isArray(val) && val.length === 0;
      const isEmptyString = typeof val === "string" && val.trim() === "";
      const isNullish = val === null || val === undefined;
      if (!isEmptyArray && !isEmptyString && !isNullish) {
        acc[key] = val;
      }
      return acc;
    }, {});
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadMoreMonsterResults = async () => {
    if (isLoadingMore || isLoading) return;
    const nextPage = page + 1;
    setPage(nextPage);
    handleSearch(nextPage);
  };

  const lastMonsterCardRef = useCallback(
    (node) => {
      if (isLoading || isLoadingMore) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            loadMoreMonsterResults();
          }
        },
        { root: null, rootMargin: "100px", threshold: 0.1 },
      );

      if (node) observer.current.observe(node);
    },
    [isLoading, isLoadingMore, hasMore, loadMoreMonsterResults],
  );

  const makeSearchCall = async (
    tokenOverride = null,
    values = formik.values,
    pageNum = 1,
    pageSize = 10,
  ) => {
    const token = tokenOverride || localStorage.getItem("monstertokenInd");
    if (!token)
      throw new Error("No Monster token found. Please authenticate first.");

    const experienceFilter =
      values.yearsExpFrom || values.yearsExpTo
        ? {
            experience: {
              ...(values.yearsExpFrom && {
                min_exp: String(values.yearsExpFrom),
              }),
              ...(values.yearsExpTo && {
                max_exp: String(values.yearsExpTo),
              }),
            },
          }
        : {};

    const allParams = {
      queries: {
        search_scope_id: 5,
        boolean: values.keywords,
      },
      portal_name: values.portalName,
      filters: {
        ...experienceFilter,
      },
      skills: values.skills,
      skillOperator: values.skillMode === "all" ? "AND" : "OR",
      designations: values.designations,
      designationOperator: values.designationMode === "all" ? "AND" : "OR",
      locations: values.locations,
      noticePeriod: values.noticePeriod,
      gender: values.gender,
      lastActiveDays: values.lastActiveDays,
      hasEmail: values.email,
      hasPhone: values.phone,
      size: pageSize,
      from: (pageNum - 1) * pageSize,
      refine_search: {
        sort_by: "relevance",
      },  
  };
    const filtered = filterEmpty(allParams);
    const payload = { reqParam: filtered };
    const params = new URLSearchParams({
      foundit_access_token: token,
    });

    return await post(MONSTER_SEARCH_BASE_URL,`${MONSTER_SEARCH}?${params.toString()}`,
      payload,
      {
        headers: { ...MonsterConfig.apiHeaders },
      },
    );
  };

  const handleSearch = async (pageNum = 1) => {
    if (pageNum === 1) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const res = await makeMonsterApiCall((token) =>
        makeSearchCall(token, formik.values, pageNum),
      );
      const parsedResponse = typeof res === "string" ? JSON.parse(res) : res;

      const all = parsedResponse?.response?.all_resumes || [];

      const newPage = all;

      setResults((prev) =>
        pageNum === 1 ? newPage : [...(prev || []), ...newPage],
      );
      setTotalCount(parsedResponse.response.total_count);
      setHasMore(
        newPage.length > 0 &&
          (results || []).length + newPage.length <
            parsedResponse.response.total_count,
      );
    } catch (err) {
      console.error("Monster search failed", err);
      Toast("Search failed. Please try again.");
    } finally {
      if (pageNum === 1) {
        setIsLoading(false);
      } else {
        setIsLoadingMore(false);
      }
    }
  };

  const setMapping = (monsterPuuid, candidateId) => {
    const map = { ...parsedMap, [monsterPuuid]: candidateId };
    setParsedMap(map);
    localStorage.setItem("monsterCandidateIdMap", JSON.stringify(map));
  };

  const retryFetchCandidate = async (
    candidateId,
    attempts = 5,
    delayMs = 5000,
    signal = null,
  ) => {
    for (let i = 0; i < attempts; i++) {
      if (signal?.aborted) return null;
      const full = await fetchCandidateById(candidateId);
      if (full && full.id) return full;
      if (signal?.aborted) return null;
      await new Promise((r) => setTimeout(r, delayMs));
    }
    return null;
  };

  const fetchCandidateById = async (id) => {
    try {
      const res = await get(VYLABS_BASE_URL, `${GET_CANDIDATE_BY_ID}?id=${id}`);
      const candidate = res?.candidate;
      const candidatefiles = res?.files;
      const parsedResumeData = candidate?.parsed_resume
        ? JSON.parse(candidate.parsed_resume)
        : null;
      const presignedUrl =
        candidatefiles && candidatefiles.length > 0
          ? candidatefiles[0].presigned_url
          : null;
      const updatedCandidate = {
        ...candidate,
        experience:
          candidate?.yearsOfExperience ??
          candidate?.years_of_experience ??
          candidate?.experience ??
          0,
        secondaryEmail: candidate?.secondary_email,
        secondaryPhone: candidate?.secondary_phone,
        lineAddress1: candidate.line_address_1,
        lineAddress2: candidate.line_address_2,
        zipCode: candidate.zip_code,
        preferredSalary: candidate.preferred_salary,
        vendorID: candidate?.vendor_id,
        parsedResumeData,
        files: candidatefiles || null,
        presignedUrl,
        isFileBased: false,
        avatar: res?.profile_img_url,
      };
      if (updatedCandidate.vendorID) {
        try {
          const vendorResponse = await get(
            VYLABS_BASE_URL,
            `${GET_VENDOR_DETAILS_BY_ID}?id=${updatedCandidate.vendorID}`,
          );
          setAssignedVendor(vendorResponse);
          updatedCandidate.vendorDetails = vendorResponse?.vendor || null;
        } catch {
          updatedCandidate.vendorDetails = null;
          setAssignedVendor(null);
        }
      } else {
        updatedCandidate.vendorDetails = null;
        setAssignedVendor(null);
      }
      return updatedCandidate;
    } catch (e) {
      console.error("fetchCandidateById error", e);
      return null;
    }
  };

  const fetchMonsterProfile = async (p_uuid, signal) => {
    const makeViewCall = async (tokenOverride = null) => {
      const token = tokenOverride || localStorage.getItem("monstertokenInd");
      if (!token)
        throw new Error("No Monster token found. Please authenticate first.");
      return await post(
        MONSTER_SEARCH_BASE_URL,
        MONSTER_VIEW,
        { p_uuid },
      );
    };
    const res = await makeMonsterApiCall(makeViewCall);
    let parsed = typeof res === "string" ? JSON.parse(res) : res;
    const profile = parsed?.data?.searchResult?.[0] || parsed?.data || parsed;
    return profile;
  };
  const handlePopupClose = () => {
    if (abortController) abortController.abort("Popup closed by user");
    try {
      if (wsRef) wsRef.close();
    } catch (e) {}
    if (wsPingInterval) clearInterval(wsPingInterval);
    if (wsPingTimeout) clearTimeout(wsPingTimeout);
    if (localCandidate?.files?.[0]?.localUrl) {
      URL.revokeObjectURL(localCandidate.files[0].localUrl);
    }
    setIsPopupOpen(false);
    setIsProcessingView(false);
    setLocalCandidate(null);
    setFallbackMonsterPopupOpen(false);
    setSelectedMonsterId(null);
    setAbortController(null);
    setIsEditingCandidate(false);
    setOriginalCandidateData(null);
    setEditedCandidateData(null);
  };

  const handleViewCandidate = async (index) => {
    if (isProcessingView) return;

    const candidate = results?.[index];
    if (!candidate) {
      Toast(
        "Could not find the specified candidate in the search results.",
        "error",
      );
      console.error("Candidate not found at index:", index);
      return;
    }

    const monsterId = candidate.p_uuid;

    if (!monsterId) {
      Toast("Candidate ID is missing.", "error");
      return;
    }

    const controller = new AbortController();
    setAbortController(controller);
    setIsPopupOpen(true);
    setIsProcessingView(true);
    setLocalCandidate(null);
    setSelectedMonsterId(monsterId);
    const appendToMonsterCandidateId = "monster_" + String(monsterId).trim();
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const notesRes = await get(
          VYLABS_BASE_URL,
          `${GET_CANDIDATES_BY_NOTES}?notes=${appendToMonsterCandidateId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          },
        );
        const data = notesRes;
        const mappedId =
          (data && data.id) ||
          (data && data.candidate && data.candidate.id) ||
          (Array.isArray(data) && data[0]?.id) ||
          null;

        if (mappedId) {
          const full = await fetchCandidateById(mappedId);
          if (full && !controller.signal.aborted) {
            setLocalCandidate(full);
            setIsProcessingView(false);
            return;
          }
        }
      } catch (err) {
        if (err?.code === "ERR_CANCELED") return;
      }
    }

    try {
      const profile = await fetchMonsterProfile(monsterId, controller.signal);
      setLastProfileDetails(profile || null);
      const resumeHtmlRaw = profile?.resume_html || profile?.resumeText;
      const generatedHtml =
        !resumeHtmlRaw &&
        (profile?.resume_text || profile?.title || profile?.skills)
          ? buildHtmlFromProfile(profile)
          : "";
      const resumeHtml = resumeHtmlRaw || generatedHtml;

      if (!resumeHtml) {
        return;
      }

      let pdfFile = null;
      try {
        pdfFile = htmlToTextFile(resumeHtml, `monster_${monsterId}.txt`);
      } catch (e) {
        console.warn("[Background Parsing] Failed to create PDF file:", e);
        return;
      }
      const transformedCandidate = transformMonsterProfileToCandidate(
        profile,
        monsterId,
      );

      if (transformedCandidate && !controller.signal.aborted) {
        const displayCandidate = {
          ...transformedCandidate.candidate,
          isTemp: true,
          source: "Monster",
          vendorID: envConfig.monsterVendorID,
          vendorDetails: null,
          files: pdfFile ? [pdfFile] : transformedCandidate.files,
          presignedUrl: transformedCandidate.files[0]?.presigned_url || null,
          isFileBased: false,
          avatar: transformedCandidate.profile_img_url,
          parsedResumeData: transformedCandidate.candidate.parsedResumeData,
          isTransformed: true,
          monsterId: monsterId,
          isLoadingResume: true,
        };
        setLocalCandidate(displayCandidate);
        setIsProcessingView(false);
        setFallbackMonsterPopupOpen(false);
        startBackgroundResumeParsing(
          monsterId,
          profile,
          controller.signal,
          displayCandidate,
          pdfFile,
          resumeHtml,
        );
        return;
      }
      setIsProcessingView(false);
      setFallbackMonsterPopupOpen(true);
    } catch (e) {
      if (controller.signal.aborted) return;
      console.error("Error viewing Monster candidate:", e);
      if (lastProfileDetails) {
        const transformedCandidate = transformMonsterProfileToCandidate(
          lastProfileDetails,
          monsterId,
        );
        if (transformedCandidate) {
          const displayCandidate = {
            ...transformedCandidate.candidate,
            vendorID: envConfig.monsterVendorID,
            vendorDetails: null,
            files: transformedCandidate.files,
            presignedUrl: transformedCandidate.files[0]?.presigned_url || null,
            isFileBased: false,
            avatar: transformedCandidate.profile_img_url,
            parsedResumeData: transformedCandidate.candidate.parsedResumeData,
            isTransformed: true,
            source: "Monster",
            monsterId: monsterId,
            isLoadingResume: true,
          };

          setLocalCandidate(displayCandidate);
          setIsProcessingView(false);
          setFallbackMonsterPopupOpen(false);
          return;
        }
      }

      setIsProcessingView(false);
      setFallbackMonsterPopupOpen(true);
    }
  };
  const startBackgroundResumeParsing = async (
    monsterId,
    profile,
    signal,
    pdfFile,
    resumeHtml,
  ) => {
    try {
      parseMonsterResumeHeadless(pdfFile, { signal })
        .then(async (parseResult) => {
          if (signal?.aborted) return;

          if (parseResult?.status === "completed" && parseResult?.candidateId) {
            setMapping(monsterId, parseResult.candidateId);
            const appendToCandidateId = "monster_" + String(monsterId).trim();
            try {
              const token = localStorage.getItem("token");
              if (token) {
                const profileData =
                  typeof profile === "string" ? JSON.parse(profile) : profile;
                const personalDetails = Array.isArray(
                  profileData?.personal_details,
                )
                  ? profileData?.personal_details?.[0]
                  : {};

                const name =
                  profileData?.name ||
                  `${personalDetails?.first_name || ""} ${
                    personalDetails?.last_name || ""
                  }`.trim() ||
                  "Unknown Name";

                const emailArray = Array.isArray(profileData?.email)
                  ? profileData?.email
                  : [];
                const primaryEmail =
                  emailArray.find((e) => e?.is_verified)?.id ||
                  emailArray[0]?.id ||
                  "";

                const mobileArray = Array.isArray(profileData?.mobile_details)
                  ? profileData?.mobile_details
                  : [];
                const primaryPhone =
                  mobileArray.find((m) => m?.verified)?.number ||
                  mobileArray[0]?.number ||
                  "";
                const countryCode = mobileArray[0]?.country_code || "91";
                const formattedPhone = primaryPhone
                  ? `+${countryCode}${primaryPhone}`
                  : "";

                const locationArray = Array.isArray(
                  profileData?.current_location,
                )
                  ? profileData?.current_location
                  : [];
                const location =
                  locationArray[0]?.text ||
                  profileData?.aggregate_locations?.[0] ||
                  "";

                let city = (location || "").split(",")[0].trim();
                let state = "";
                let country = "";

                if (profileData?.current_location_primary_group_tag) {
                  country = profileData.current_location_primary_group_tag;
                } else if (profileData?.current_country?.text) {
                  country = profileData.current_country.text;
                } else if (
                  Array.isArray(profileData?.nationality) &&
                  profileData.nationality.length > 0
                ) {
                  country = profileData.nationality[0]?.text || "";
                }

                const currentEmployment = Array.isArray(
                  profileData?.current_employment,
                )
                  ? profileData?.current_employment?.[0]
                  : profileData?.current_employment || {};
                const designation =
                  currentEmployment?.designation?.text ||
                  currentEmployment?.designation ||
                  "";

                const experienceArray = Array.isArray(profileData?.experience)
                  ? profileData?.experience
                  : [];
                const finalExperienceArray = Array.isArray(
                  profileData?.total_experience,
                )
                  ? profileData?.total_experience
                  : [];
                const yearsExp =
                  experienceArray[0]?.value ||
                  finalExperienceArray[0]?.value ||
                  profileData?.experience?.[0]?.value ||
                  0;

                const updatePayload = {
                  id: parseResult.candidateId,
                  vendorID: envConfig.monsterVendorID,
                  notes: appendToCandidateId,
                  name: name,
                  email: primaryEmail,
                  phone: formattedPhone,
                  city: city,
                  state: state,
                  country: country,
                  designation: designation,
                  yearsOfExperience: yearsExp,
                  status: "New Profile",
                  source: "Monster",
                };
                Object.keys(updatePayload).forEach((key) => {
                  if (updatePayload[key] === "" || updatePayload[key] == null) {
                    delete updatePayload[key];
                  }
                });
                await post(
                  VYLABS_BASE_URL,
                  UPDATE_CANDIDATE_DETAILS,
                  updatePayload,
                  { headers: { Authorization: `Bearer ${token}` } },
                );
              }
            } catch (updateErr) {
              console.error(
                "Error updating Monster candidate with notes:",
                updateErr,
              );
            }

            const full = await retryFetchCandidate(
              parseResult.candidateId,
              5,
              5000,
              signal,
            );
            if (full && !signal?.aborted) {
              setLocalCandidate((prev) => ({
                ...prev,
                ...full,
                isTemp: false,
                isLoadingResume: false,
                isTransformed: false,
              }));
            }
          } else if (
            parseResult?.status === "incomplete" ||
            parseResult?.status === "failed"
          ) {
            const newCandidateId = await createCandidateFromFallback(
              monsterId,
              parseResult.fileContent || resumeHtml,
              profile,
              parseResult.fileId,
              parseResult.status,
            );

            if (newCandidateId && !signal?.aborted) {
              if (parseResult.fileId) {
                try {
                  await post(VYLABS_BASE_URL, LINK_CANDIDATE_RESUME, {
                    candidate_id: newCandidateId,
                    file_id: parseResult.fileId,
                  });
                } catch (e) {
                  console.error("Link resume failed", e);
                }
              }

              setMapping(monsterId, newCandidateId);
              const full = await retryFetchCandidate(
                newCandidateId,
                5,
                5000,
                signal,
              );
              if (full && !signal?.aborted) {
                setLocalCandidate((prev) => ({
                  ...prev,
                  ...full,
                  isLoadingResume: false,
                  isTransformed: false,
                }));
              }
            } else {
              setLocalCandidate((prev) => ({
                ...prev,
                isLoadingResume: false,
              }));
            }
          } else if (parseResult?.error) {
            console.warn(
              "[Background Parsing] Parsing failed:",
              parseResult.error,
            );
            const newCandidateId = await createCandidateFromFallback(
              monsterId,
              parseResult.fileContent || resumeHtml,
              profile,
              parseResult.fileId,
              "failed",
            );

            if (newCandidateId && !signal?.aborted) {
              setMapping(monsterId, newCandidateId);
              const full = await retryFetchCandidate(
                newCandidateId,
                5,
                5000,
                signal,
              );
              if (full && !signal?.aborted) {
                setLocalCandidate((prev) => ({
                  ...prev,
                  ...full,
                  isLoadingResume: false,
                  isTransformed: false,
                }));
              }
            } else {
              setLocalCandidate((prev) => ({
                ...prev,
                isLoadingResume: false,
              }));
            }
          }
        })
        .catch((err) => {
          if (signal?.aborted) return;
          console.error("[Background Parsing] Error:", err);
          setLocalCandidate((prev) => ({
            ...prev,
            isLoadingResume: false,
          }));
        });
    } catch (error) {
      if (signal?.aborted) return;
      console.error("[Background Parsing] Setup error:", error);
      setLocalCandidate((prev) => ({
        ...prev,
        isLoadingResume: false,
      }));
    }
  };
  const transformMonsterProfileToCandidate = (profile, monsterId) => {
    try {
      const profileData =
        typeof profile === "string" ? JSON.parse(profile) : profile;
      const personalDetails = Array.isArray(profileData?.personal_details)
        ? profileData?.personal_details?.[0]
        : {};

      const name =
        profileData?.name ||
        `${personalDetails?.first_name || ""} ${
          personalDetails?.last_name || ""
        }`.trim() ||
        "Unknown Name";
      const emailArray = Array.isArray(profileData?.email)
        ? profileData?.email
        : [];
      const primaryEmail =
        emailArray.find((e) => e?.is_verified)?.id || emailArray[0]?.id || "";
      const mobileArray = Array.isArray(profileData?.mobile_details)
        ? profileData?.mobile_details
        : [];
      const primaryPhone =
        mobileArray.find((m) => m?.verified)?.number ||
        mobileArray[0]?.number ||
        "";
      const countryCode = mobileArray[0]?.country_code || "91";
      const formattedPhone = primaryPhone
        ? `+${countryCode}${primaryPhone}`
        : "";
      const locationArray = Array.isArray(profileData?.current_location)
        ? profileData?.current_location
        : [];
      const location =
        locationArray[0]?.text || profileData?.aggregate_locations?.[0] || "";
      let country = "";
      if (profileData?.current_location_primary_group_tag) {
        country = profileData.current_location_primary_group_tag;
      } else if (profileData?.current_country?.text) {
        country = profileData.current_country.text;
      } else if (
        Array.isArray(profileData?.nationality) &&
        profileData.nationality.length > 0
      ) {
        country = profileData.nationality[0]?.text || "";
      }
      const skills = [];
      if (Array.isArray(profileData?.skills_new)) {
        profileData?.skills_new?.forEach((skill) => {
          if (skill?.value && typeof skill?.value === "string") {
            skills.push(skill.value);
          }
        });
      }
      if (Array.isArray(profileData?.skills)) {
        profileData?.skills?.forEach((skill) => {
          if (skill?.text && typeof skill?.text === "string") {
            skills.push(skill.text);
          }
        });
      }
      if (Array.isArray(profileData?.it_skills)) {
        profileData?.it_skills?.forEach((skill) => {
          if (skill?.text && typeof skill?.text === "string") {
            skills.push(skill.text);
          }
        });
      }
      const uniqueSkills = [...new Set(skills.filter(Boolean))];
      const experienceArray = Array.isArray(profileData?.experience)
        ? profileData?.experience
        : [];
      const finalExperienceArray = Array.isArray(profileData?.final_experience)
        ? profileData?.final_experience
        : [];
      const yearsExp =
        experienceArray[0]?.value ||
        finalExperienceArray[0]?.value ||
        profileData?.experience?.[0]?.value ||
        0;
      const currentEmployment = Array.isArray(profileData?.current_employment)
        ? profileData?.current_employment?.[0]
        : {};

      const designation =
        currentEmployment?.designation?.text ||
        currentEmployment?.designation ||
        "";

      const employer =
        currentEmployment?.employer?.text || currentEmployment?.employer || "";
      const educationArray = Array.isArray(profileData?.educations)
        ? profileData?.educations
        : [];
      const parsedResume = {
        BasicInfo: {
          Name: name,
          Email: primaryEmail,
          Phone: formattedPhone,
          CurrentLocationCity: location?.split(",")?.[0]?.trim() || location,
          CurrentLocationCountry: "",
          YearsOfExperience: parseFloat(yearsExp) || 0,
        },
        Skills: uniqueSkills,
        EducationHistory: educationArray
          .map((edu) => ({
            Institute: edu?.college || "",
            InstituteLocationCity: "",
            InstituteLocationCountry: "",
            Degree: edu?.degree || "",
            FieldOfStudy: edu?.specialization || "",
            GraduationDate: edu?.passing_year
              ? edu?.passing_year?.toString()
              : "",
            GPAOrTestScore: -1,
          }))
          .filter((edu) => edu.Institute || edu.Degree),
        CertificationsAndLicenses: Array.isArray(
          profileData?.courses_and_certifications,
        )
          ? profileData?.courses_and_certifications?.map((cert) => ({
              CertificationName: cert?.name || "",
            }))
          : [],
        CareerHistory: {
          JobDetails: [],
        },
        ProfileSummary:
          profileData?.title?.[0]?.value ||
          profileData?.headline?.[0]?.value ||
          "",
      };
      if (Array.isArray(profileData?.previous_employments)) {
        parsedResume.CareerHistory.JobDetails =
          profileData?.previous_employments
            .filter((job) => job?.is_searchable !== false)
            .map((job, index) => {
              const isCurrent = index === 0 && job?.is_current === true;
              return {
                JobTitle: job?.designation?.text || job?.designation || "",
                JobCompany: job?.employer?.text || job?.employer || "",
                JobLocationCity: job?.employer_location?.city || "",
                JobLocationCountry: job?.employer_location?.country_code || "",
                StartDate: job?.start_date || "",
                EndDate: isCurrent ? "" : job?.end_date || "",
                CurrentlyWorking: isCurrent,
                Description: job?.description || "",
              };
            })
            .filter((job) => job.JobTitle || job.JobCompany);
      }
      const candidate = {
        id: `temp_monster_${monsterId}`,
        name: name,
        email: primaryEmail,
        secondary_email: "",
        phone: formattedPhone,
        secondary_phone: "",
        city: location?.split(",")?.[0]?.trim() || location,
        state: "",
        country: country,
        parsed_resume: JSON.stringify(parsedResume),
        skills: uniqueSkills,
        status: "New Profile",
        contact_person: "",
        contact_designation: "",
        notes: `monster_${monsterId}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        application_ids: [],
        file_id: null,
        user_id: "",
        profile_image_key: null,
        preferred_salary: null,
        line_address_1: null,
        line_address_2: null,
        zip_code: null,
        vendor_id: envConfig.monsterVendorID,
        tenant_id: "",
        vendorID: envConfig.monsterVendorID,
        designation: designation,
        employer: employer,
        experience: parseFloat(yearsExp) || 0,
        parsedResumeData: parsedResume,
        isFileBased: false,
        avatar: profileData?.image_url?.[0]?.value
          ? `https://media.monsterindia.com/${profileData?.image_url?.[0]?.value}`
          : null,
        source: "Monster",
      };
      const files = [];
      if (profileData?.resume_file_download_url) {
        files.push({
          id: `monster_file_${monsterId}`,
          name: `${name?.replace(/\s+/g, "_")}_monster_resume.pdf`,
          presigned_url: profileData?.resume_file_download_url,
        });
      }

      return {
        candidate: candidate,
        files: [],
        profile_img_url: candidate.avatar,
      };
    } catch (error) {
      console.error("Error transforming Monster profile:", error);
      return null;
    }
  };
  const formik = useFormik({
    initialValues: {
      keywords: "",
      portalName: "",
      skills: [],
      skillMode: "any",
      locations: [],
      designations: [],
      designationMode: "any",
      noticePeriod: "",
      yearsExpFrom: "",
      yearsExpTo: "",
      gender: "",
      lastActiveDays: "",
      email: false,
      phone: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      const hasKeywords = values.keywords.trim() !== "";

      if (!hasKeywords) {
        Toast("error", "Please fill all required fields correctly.");
        return;
      }
      setIsLoading(true);
      setPage(1);
      setResults([]);
      setHasSearched(true);
      setSearchQuery(values.keywords);
      await handleSearch(1);
      setIsLoading(false);
    },
  });

  return (
    <div>
      <div className="header-main-container">
        <img
          alt=""
          src={isDarkMode ? CandidateDarkIcon : CandidateLightIcon}
          className="header-title-icon"
        />
        <h2 className="monster-search-title">Monster Search ( IND )</h2>
        <Button
          label="Back to Vendor"
          style={{ padding: "5px 10px", marginLeft: "auto" }}
          event={() => navigate("/vendors")}
          iconSrc={backArrowIcon}
        />
      </div>
      <div className="advanced-search-toggle">
        <h4 onClick={toggleAdvancedSearch} className="advance-search-text">
          {showAdvancedSearch ? "Hide Advanced Search" : "Advanced Search"}
        </h4>
        <CustomTooltip
          title={
            showAdvancedSearch ? "Hide advanced fields" : "Show advanced fields"
          }
          arrow
        >
          <img
            alt="infoIcon"
            src={infoIcon}
            style={{ cursor: "pointer", width: "16px", height: "16px" }}
          />
        </CustomTooltip>
      </div>
      <div className="monster-search-container">
        <form onSubmit={formik.handleSubmit}>
          <div className="default-search-fields">
            <TextInput
              label="Keywords"
              name="keywords"
              placeholder="e.g. Java Developer"
              value={formik.values.keywords}
              onChange={formik.handleChange}
              error={formik.touched.keywords && formik.errors.keywords}
              required={true}
            />
          </div>
          <div className="default-search-fields">
            <TextInput
              name="locations"
              label="Locations"
              placeholder="e.g. Chennai, Mumbai"
              value={formik.values.locations}
              onChange={formik.handleChange}
              error={formik.touched.locations && formik.errors.locations}
            />
          </div>

          {showAdvancedSearch && (
            <>
              <div className="notice-period-field">
                <label className="notice-period-field-label">Gender</label>
                <SelectInput
                  name="gender"
                  className="notice-period-field-select"
                  options={MonsterGenders}
                  value={formik.values.gender}
                  onChange={formik.handleChange}
                />
              </div>

              <div className="field-group">
                <label>Years of Experience</label>
                <div className="exp-range">
                  <TextInput
                    name="yearsExpFrom"
                    placeholder="From ( Min )"
                    value={formik.values.yearsExpFrom}
                    onChange={formik.handleChange}
                  />
                  <TextInput
                    name="yearsExpTo"
                    placeholder="To ( Max )"
                    value={formik.values.yearsExpTo}
                    onChange={formik.handleChange}
                  />
                </div>
              </div>

              <div className="field-group" style={{ marginTop: "-15px" }}>
                <label>Designation(s)</label>
                <div className="mode-toggle" style={{ marginBottom: "-10px" }}>
                  <label>
                    <input
                      type="radio"
                      name="designationMode"
                      value="any"
                      checked={formik.values.designationMode === "any"}
                      onChange={formik.handleChange}
                    />
                    Any (OR)
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="designationMode"
                      value="all"
                      checked={formik.values.designationMode === "all"}
                      onChange={formik.handleChange}
                    />
                    All (AND)
                  </label>
                </div>
                <div className="form-group">
                  <AutoComplete
                    key={`designation-autocomplete-${autoCompleteKey}`}
                    multiple
                    placeholder="e.g. Software Engineer, Project Manager"
                    options={skills.filter((skill) => {
                      const selectedSkills = Array.isArray(formik.values.skills)
                        ? formik.values.skills
                        : [];
                      return !selectedSkills.some(
                        (selectedSkill) =>
                          selectedSkill.toLowerCase() ===
                          skill.title.toLowerCase(),
                      );
                    })}
                    tags={true}
                    name="canDesignations"
                    style={{ marginTop: "15px !important" }}
                    value={formik.values.designations || []}
                    onChange={(selectedOptions) => {
                      const validDesignations = selectedOptions
                        .map((option) =>
                          typeof option === "string" ? option : option.title,
                        )
                        .filter((option) => option.trim() !== "");
                      const uniqueDesignations = validDesignations.filter(
                        (designation, index, self) =>
                          index ===
                          self.findIndex(
                            (d) =>
                              d.toLowerCase() === designation.toLowerCase(),
                          ),
                      );
                      formik.setFieldValue("designations", uniqueDesignations);
                    }}
                    onBlur={() => formik.setFieldTouched("designations", true)}
                    InputWrapperStyle={{
                      height: "100px",
                      overflowY: "auto",
                      padding: "10px",
                      marginBottom: "30px",
                      marginTop: "15px",
                    }}
                    showClearAll={true}
                    error={
                      formik.touched.designations && formik.errors.designations
                    }
                    errorMessage={formik.errors.designations}
                  />
                </div>
              </div>

              <div className="field-group" style={{ marginTop: "-15px" }}>
                <label>Skills</label>
                <div className="mode-toggle" style={{ marginBottom: "-10px" }}>
                  <label>
                    <input
                      type="radio"
                      name="skillMode"
                      value="any"
                      checked={formik.values.skillMode === "any"}
                      onChange={formik.handleChange}
                    />
                    Any (OR)
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="skillMode"
                      value="all"
                      checked={formik.values.skillMode === "all"}
                      onChange={formik.handleChange}
                    />
                    All (AND)
                  </label>
                </div>
                <div className="form-group">
                  <AutoComplete
                    key={`auto-complete-${autoCompleteKey}`}
                    multiple
                    placeholder="e.g. JavaScript, React, Node.js"
                    options={skills.filter((skill) => {
                      const selectedSkills = Array.isArray(formik.values.skills)
                        ? formik.values.skills
                        : [];
                      return !selectedSkills.some(
                        (selectedSkill) =>
                          selectedSkill.toLowerCase() ===
                          skill.title.toLowerCase(),
                      );
                    })}
                    tags={true}
                    name="canSkills"
                    style={{ marginTop: "15px !important" }}
                    value={formik.values.skills || []}
                    onChange={(selectedOptions) => {
                      const validSkills = selectedOptions
                        .map((option) =>
                          typeof option === "string" ? option : option.title,
                        )
                        .filter((option) => option.trim() !== "");
                      const uniqueSkills = validSkills.filter(
                        (skill, index, self) =>
                          index ===
                          self.findIndex(
                            (s) => s.toLowerCase() === skill.toLowerCase(),
                          ),
                      );
                      formik.setFieldValue("skills", uniqueSkills);
                    }}
                    onBlur={() => formik.setFieldTouched("skills", true)}
                    InputWrapperStyle={{
                      height: "100px",
                      overflowY: "auto",
                      padding: "10px",
                      marginBottom: "30px",
                      marginTop: "15px",
                    }}
                    showClearAll={true}
                    error={formik.touched.skills && formik.errors.skills}
                    errorMessage={formik.errors.skills}
                  />
                </div>
              </div>

              <div
                className="notice-period-field"
                style={{ marginTop: "-20px" }}
              >
                <label className="notice-period-field-label">
                  Notice Period
                </label>
                <SelectInput
                  name="noticePeriod"
                  className="notice-period-field-select"
                  options={MonsterNoticePeriods}
                  value={formik.values.noticePeriod}
                  onChange={formik.handleChange}
                />
              </div>

              <div
                className="last-active-days-field"
                style={{ marginTop: "-20px" }}
              >
                <TextInput
                  label="Last Active Days"
                  name="lastActiveDays"
                  placeholder="e.g. 30"
                  value={formik.values.lastActiveDays}
                  onChange={formik.handleChange}
                />
              </div>

              <div className="checkbox-group" style={{ marginTop: "-45px" }}>
                <DynamicCheckbox
                  label="Has Phone Number"
                  name="phone"
                  checked={formik.values.phone}
                  isDarkMode={isDarkMode}
                  onChange={formik.handleChange}
                />
                <DynamicCheckbox
                  label="Has Email"
                  name="email"
                  checked={formik.values.email}
                  isDarkMode={isDarkMode}
                  onChange={formik.handleChange}
                />
              </div>
            </>
          )}
        </form>
      </div>

      <div className="full-width">
        <Button
          label={isLoading ? "Searching..." : "Search Candidates"}
          event={formik.handleSubmit}
          disabled={isLoading}
        />
      </div>

      {isLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "40vh",
          }}
        >
          <VybogTALLoaderAnimation />
        </div>
      ) : (
        results && (
          <div className="monster-results-section">
            <div className="monster-candidates-grid">
              {results.map((candidate, index) => {
                const isLast = index === results.length - 1;

                const skills =
                  candidate?.skills?.map((s) => s?.text || s?.value || s) || [];

                const experience =
                  candidate?.experience?.[0]?.value ||
                  candidate?.total_experience?.[0]?.value ||
                  candidate?.experience?.value ||
                  candidate?.total_experience?.value ||
                  candidate?.experience ||
                  candidate?.total_experience ||
                  0;

                const role =
                  candidate?.current_employment?.[0]?.designation?.text ||
                  candidate?.current_employment?.[0]?.designation ||
                  candidate?.current_employment?.designation?.text ||
                  candidate?.current_employment?.designation ||
                  candidate?.title ||
                  "Not specified";

                const location =
                  candidate?.current_location?.[0]?.text ||
                  candidate?.aggregate_locations?.[0] ||
                  "N/A";

                const name =
                  candidate?.name ||
                  `${candidate?.personal_details?.[0]?.first_name || ""} ${
                    candidate?.personal_details?.[0]?.last_name || ""
                  }`.trim();

                return (
                  <div
                    key={candidate.p_uuid}
                    ref={isLast ? lastMonsterCardRef : null}
                  >
                    <Card
                      name={name}
                      role={role}
                      experience={experience}
                      location={location}
                      skills={skills}
                      viewEvent={() => handleViewCandidate(index)}
                      showView={true}
                      cardbackgroundColor="var(--card__background)"
                      cardColor="var(--header__color)"
                      skillbackgroundColor="var(--skill-badge-bg)"
                      skillColor="var(--skill-badge-color)"
                      showAssignJob={false}
                    />
                  </div>
                );
              })}

              {isLoadingMore && (
                <>
                  {Array.from({ length: 10 }).map((_, index) => (
                    <DynamicSkeleton
                      key={index}
                      variant="rectangular"
                      dimensions={{
                        width: "100%",
                        height: "170px",
                        sx: { borderRadius: "16px" },
                      }}
                    />
                  ))}
                </>
              )}

              {!hasMore && results.length > 0 && (
                <div
                  style={{
                    textAlign: "center",
                    width: "100%",
                    color: "#666",
                    marginTop: 12,
                  }}
                >
                  <p>No more candidates to load</p>
                </div>
              )}

              <div>
                {isPopupOpen && !fallbackMonsterPopupOpen && (
                  <SidePopup
                    key={localCandidate?.id || "monster-processing"}
                    isOpen={isPopupOpen}
                    onClose={handlePopupClose}
                    title="Candidate Details"
                    sidePopupWidth={"82%"}
                  >
                    {isProcessingView ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "80vh",
                        }}
                      >
                        <VybogTALLoaderAnimation />
                      </div>
                    ) : (
                      localCandidate?.id && (
                        <CandidateDetailsContainer
                          candidate={localCandidate}
                          activeTab={0}
                          handleTabChange={() => {}}
                          hasSearched={false}
                          isEditingCandidate={isEditingCandidate}
                          handleEditCandidate={handleEditCandidate}
                          saveCandidateChanges={saveCandidateChanges}
                          resetCandidateEdit={resetCandidateEdit}
                          handleCandidateChange={handleCandidateChange}
                          editedCandidateData={editedCandidateData}
                          vendorList={vendorList}
                          vendorOptionLoading={vendorOptionLoading}
                          handleVendorInputChange={handleVendorInputChange}
                          assignedVendor={assignedVendor}
                        />
                      )
                    )}
                  </SidePopup>
                )}
              </div>
            </div>

            {hasSearched && results && results.length === 0 && !isLoading && (
              <div className="monster-no-search-result">
                <h3>No candidates were found in the results of your search.</h3>
                <AnimationPlayer
                  animationSrc={NoDataFound}
                  playerHeight="250px"
                  playerWidth="250px"
                />
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
}

export default MonsterSearch;
